import { BarChart2, BookOpen, Plus, Trophy, Users } from "lucide-react";
import { NavLink, useNavigate } from "react-router-dom";
import { cn } from "@/utils/cn";
import { useState } from "react";
import { NewSessionModal } from "@/components/sessions/NewSessionModal";

interface BottomNavProps {
  pendingRequests: number;
  hasUnreadComments: boolean;
}

export function BottomNav({ pendingRequests, hasUnreadComments }: BottomNavProps) {
  const [showNewSession, setShowNewSession] = useState(false);
  const navigate = useNavigate();

  const navItem = (to: string, icon: React.ReactNode, label: string) => (
    <NavLink
      to={to}
      className={({ isActive }) =>
        cn(
          "flex flex-col items-center gap-1 text-xs py-2 px-3 transition-colors",
          isActive ? "text-accent" : "text-text-muted"
        )
      }
    >
      {icon}
      <span>{label}</span>
    </NavLink>
  );

  return (
    <>
      {/* min-h (not h) so padding-bottom grows the box beyond the home-indicator
          inset instead of squeezing the icon/label content that sits inside it. */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 bg-bg-base border-t border-white/10 flex justify-around items-center min-h-16 z-40 pb-[env(safe-area-inset-bottom)] pl-[env(safe-area-inset-left)] pr-[env(safe-area-inset-right)]">
        {navItem("/logbook", <BookOpen size={22} />, "Logbook")}
        {navItem("/stats", <BarChart2 size={22} />, "Stats")}

        {/* FAB-style Log button */}
        <button
          onClick={() => setShowNewSession(true)}
          className="flex flex-col items-center gap-1 -mt-5"
          aria-label="Log a session"
        >
          <div className="bg-accent rounded-full w-14 h-14 flex items-center justify-center shadow-lg shadow-accent/30">
            <Plus size={28} strokeWidth={2.5} className="text-white" />
          </div>
          <span className="text-xs text-text-muted mt-0.5">Log</span>
        </button>

        {navItem("/challenges", <Trophy size={22} />, "Challenges")}
        <NavLink
          to="/crew"
          className={({ isActive }) =>
            cn(
              "flex flex-col items-center gap-1 text-xs py-2 px-3 transition-colors relative",
              isActive ? "text-accent" : "text-text-muted"
            )
          }
        >
          <div className="relative">
            <Users size={22} />
            {pendingRequests > 0 && (
              <span className="absolute -top-1 -right-1.5 w-4 h-4 rounded-full bg-accent text-white text-[9px] font-bold flex items-center justify-center">
                {pendingRequests > 9 ? "9+" : pendingRequests}
              </span>
            )}
            {hasUnreadComments && pendingRequests === 0 && (
              <span className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-accent" />
            )}
          </div>
          <span>Crew</span>
        </NavLink>
      </nav>

      {showNewSession && (
        <NewSessionModal
          onClose={() => setShowNewSession(false)}
          onCreated={(id) => {
            setShowNewSession(false);
            navigate(`/sessions/${id}`);
          }}
        />
      )}
    </>
  );
}
